import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Env from "../../runtimeEnv";
import Navbar from "../../components/Navbar";

const displayValue = (value) => value || "Not available";

export default function AdminAccountLookup() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    const email = new FormData(event.currentTarget).get("email").trim();
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const response = await axios.post(
        `${Env.REACT_APP_PYTHON_API_URL}/api/admin/account-lookup`,
        { email },
        { withCredentials: true }
      );
      setResult(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to look up the account.");
    } finally {
      setLoading(false);
    }
  };

  const isConfirmed = result?.status === "CONFIRMED";
  const fullName = [result?.firstName, result?.lastName].filter(Boolean).join(" ");
  const details = result?.exists ? [
    {
      label: "Account status",
      value: `${result.statusLabel} (${result.status})`,
      tileClass: isConfirmed
        ? "border border-green-300 bg-green-50"
        : "border border-red-300 bg-red-50",
      valueClass: isConfirmed ? "text-green-700" : "text-red-700",
    },
    { label: "Created", value: result.createdAt ? new Date(result.createdAt).toLocaleString() : null },
    {
      label: "Enabled",
      value: result.enabled ? "Yes" : "No",
      valueClass: result.enabled ? "text-green-700" : "text-red-700",
    },
    { label: "Full name", value: fullName },
    { label: "Account type", value: result.accountType },
    { label: "User ID", value: result.userId },
  ] : [];

  return (
    <div>
      <Navbar />
      <div className="mt-20 admin-container">
        <div className="max-w-2xl w-full shadow-lg rounded-lg p-6 space-y-6">
          <h2 className="text-3xl font-semibold text-gray-800 text-center">Account Lookup</h2>
          <Link to="/admin" className="text-blue-600 hover:underline">← Back to admin</Link>
          <form onSubmit={submit} className="space-y-4">
            <label className="block font-medium">
              Email
              <input name="email" type="email" autoComplete="email" required className="input-field" />
            </label>
            <button type="submit" disabled={loading} className="button-primary">
              {loading ? "Looking up..." : "Look Up Account"}
            </button>
          </form>
          {error && <p role="alert" className="text-red-600">{error}</p>}
          {result && !result.exists && (
            <div role="status" className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">
              No Cognito account exists for <strong>{result.email}</strong>.
            </div>
          )}
          {result?.exists && (
            <section aria-label="Account details" className="space-y-4">
              <p role="status" className="rounded-lg border border-green-300 bg-green-50 p-4 text-green-900">
                Cognito account found for <strong>{result.email}</strong>.
              </p>
              <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {details.map(({ label, value, tileClass = "bg-gray-50", valueClass = "text-gray-900" }) => (
                  <div key={label} className={`rounded-lg p-3 ${tileClass}`}>
                    <dt className="text-sm font-medium text-gray-500">{label}</dt>
                    <dd className={`mt-1 break-words font-medium ${valueClass}`}>{displayValue(value)}</dd>
                  </div>
                ))}
              </dl>
              {result.accountType?.toLowerCase().includes("client") && (
                <div className="rounded-lg bg-gray-50 p-3">
                  <h3 className="text-sm font-medium text-gray-500">Assigned therapist</h3>
                  {result.assignedTherapists.length ? (
                    <ul className="mt-1 space-y-1">
                      {result.assignedTherapists.map((therapist) => (
                        <li key={therapist.userId} className="break-words">
                          {therapist.name || "Name unavailable"}
                          {therapist.email ? ` — ${therapist.email}` : ""}
                          {` (${therapist.userId})`}
                        </li>
                      ))}
                    </ul>
                  ) : <p className="mt-1">Not assigned</p>}
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
